import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import {
  DEFAULT_BAAS_API_LOGS_HIDE_PREFS,
  EMPTY_BAAS_API_LOGS_FILTERS,
  filterBaasApiLogs,
  loadBaasApiLogsHidePrefs,
  saveBaasApiLogsHidePrefs,
} from "../baasApiLogsFilters";
import { BaasApiLogsFilterBar } from "./BaasApiLogsFilterBar";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(date);
};

const statusClass = (statusCode) => {
  if (statusCode == null) return "text-gray-500";
  if (statusCode >= 500) return "text-red-600 dark:text-red-400";
  if (statusCode >= 400) return "text-amber-600 dark:text-amber-400";
  return "text-emerald-600 dark:text-emerald-400";
};

export const BaasApiLogsPanel = ({
  logs = [],
  loading = false,
  emptyMessage,
  filterPreset = null,
  filterPresetKey = 0,
}) => {
  const [filters, setFilters] = useState(EMPTY_BAAS_API_LOGS_FILTERS);
  const [hidePrefs, setHidePrefs] = useState(() => loadBaasApiLogsHidePrefs());

  useEffect(() => {
    if (!filterPreset) return;
    setFilters({
      ...EMPTY_BAAS_API_LOGS_FILTERS,
      ...filterPreset,
    });
  }, [filterPresetKey, filterPreset]);

  const handleHidePrefsChange = useCallback((nextPrefs) => {
    const normalized = {
      hideCarteira: Boolean(nextPrefs?.hideCarteira),
      hidePrivateRoutes: Boolean(nextPrefs?.hidePrivateRoutes),
    };
    setHidePrefs(normalized);
    saveBaasApiLogsHidePrefs(normalized);
  }, []);

  const filteredLogs = useMemo(
    () => filterBaasApiLogs(logs, filters, hidePrefs || DEFAULT_BAAS_API_LOGS_HIDE_PREFS),
    [logs, filters, hidePrefs],
  );

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
        {intl.get("baas.api.logs.loading")}
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4">
      <BaasApiLogsFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        hidePrefs={hidePrefs}
        onHidePrefsChange={handleHidePrefsChange}
        filteredItems={filteredLogs}
      />

      {!logs.length ? (
        <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {emptyMessage || intl.get("baas.api.logs.empty")}
        </div>
      ) : !filteredLogs.length ? (
        <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {intl.get("baas.api.logs.emptyFiltered")}
        </div>
      ) : (
        <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="hidden border-b border-gray-100 bg-gray-50/80 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40 lg:grid lg:grid-cols-[minmax(0,0.45fr)_minmax(0,0.15fr)_minmax(0,1fr)_minmax(0,0.15fr)_minmax(0,0.15fr)_minmax(0,0.35fr)]">
            <span>{intl.get("baas.api.logs.date")}</span>
            <span>{intl.get("baas.api.logs.method")}</span>
            <span>{intl.get("baas.api.logs.path")}</span>
            <span>{intl.get("baas.api.logs.status")}</span>
            <span>{intl.get("baas.api.logs.duration")}</span>
            <span>{intl.get("baas.api.logs.correlation")}</span>
          </div>

          {filteredLogs.map((entry) => (
            <div
              key={entry.id}
              className="grid grid-cols-1 gap-2 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-brand-dark-border lg:grid-cols-[minmax(0,0.45fr)_minmax(0,0.15fr)_minmax(0,1fr)_minmax(0,0.15fr)_minmax(0,0.15fr)_minmax(0,0.35fr)] lg:items-center"
            >
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.logs.date")}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {formatDateTime(entry.date)}
                </p>
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.logs.method")}
                </p>
                <span className="inline-flex rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-brand-dark-border dark:text-gray-200">
                  {entry.method}
                </span>
              </div>
              <div className="min-w-0">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.logs.path")}
                </p>
                <code className="block truncate text-sm text-gray-600 dark:text-gray-300">
                  {entry.path}
                </code>
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.logs.status")}
                </p>
                <p className={`text-sm font-semibold ${statusClass(entry.statusCode)}`}>
                  {entry.statusCode ?? "—"}
                </p>
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.logs.duration")}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {entry.durationMs != null ? `${entry.durationMs} ms` : "—"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.logs.correlation")}
                </p>
                <code className="block truncate text-xs text-gray-500 dark:text-gray-400">
                  {entry.correlationId || "—"}
                </code>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
